/**
 * <product-waitlist-form> — pre-launch waitlist capture.
 *
 * Collects first name, last name, email, phone (+ optional preferred storage/colour)
 * and POSTs them as FormData to a Google Apps Script web app, which appends the row
 * to a Google Sheet. Same transport as the Starlink lead form and the notify-me
 * (restock) form: `mode: 'no-cors'`, so the response is opaque — a resolved fetch
 * means "the request left the browser", which is the best a no-cors POST can report.
 *
 * Spam handling mirrors notify-me: a hidden honeypot field plus a dwell-time gate.
 * Both fake a success state rather than showing an error, so a bot gets no signal.
 */
class ProductWaitlistForm extends HTMLElement {
  constructor() {
    super();
    this.mountedAt = Date.now();
    this.submitting = false;
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '';
    this.form = this.querySelector('[data-pw-form]');
    this.honeypot = this.querySelector('[data-pw-hp]');
    this.submitBtn = this.querySelector('[data-pw-submit]');
    this.apiError = this.querySelector('[data-pw-api-error]');
    this.successEl = this.querySelector('[data-pw-success]');
    this.strings = (window.theme && window.theme.strings && window.theme.strings.productWaitlist) || {};

    this.buildWhatsAppLink();

    if (!this.form) return;
    this.form.addEventListener('submit', this.handleSubmit);

    // Clear a field's error as soon as the shopper starts fixing it.
    this.form.querySelectorAll('[data-pw-required], [data-pw-phone]').forEach((input) => {
      input.addEventListener('input', () => this.clearFieldError(input));
    });
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
  }

  /**
   * Build the wa.me link in JS so the product name is encoded exactly once and
   * titles containing & ' " survive. The server-rendered href is a plain
   * wa.me/<number> fallback for the no-JS case.
   */
  buildWhatsAppLink() {
    const waBtn = this.querySelector('[data-pw-wa]');
    if (!waBtn) return;
    const number = (waBtn.dataset.waNumber || '').trim();
    if (!number) return;
    const msg = ((waBtn.dataset.waPrefix || '') + ' ' + (waBtn.dataset.waProduct || '')).trim();
    waBtn.href = 'https://wa.me/' + number + (msg ? '?text=' + encodeURIComponent(msg) : '');
  }

  static isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  /** Loose international check: 7–15 digits once separators are stripped (ITU E.164 max). */
  static isValidPhone(value) {
    const digits = value.replace(/[^\d]/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }

  fieldError(input) {
    const field = input.closest('.pw__field');
    return field ? field.querySelector('[data-pw-error]') : null;
  }

  setFieldError(input, message) {
    const el = this.fieldError(input);
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    input.setAttribute('aria-invalid', 'true');
  }

  clearFieldError(input) {
    const el = this.fieldError(input);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    input.removeAttribute('aria-invalid');
  }

  showApiError(message) {
    if (!this.apiError) return;
    this.apiError.textContent = message;
    this.apiError.hidden = false;
  }

  clearApiError() {
    if (!this.apiError) return;
    this.apiError.textContent = '';
    this.apiError.hidden = true;
  }

  setLoading(isLoading) {
    this.submitting = isLoading;
    if (!this.submitBtn) return;
    this.submitBtn.disabled = isLoading;
    this.submitBtn.classList.toggle('pw__submit--loading', isLoading);
    this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  showSuccess() {
    if (this.form) this.form.hidden = true;
    const whatsapp = this.querySelector('.pw__whatsapp');
    if (whatsapp) whatsapp.hidden = true;
    const heading = this.querySelector('.pw__card-heading');
    const text = this.querySelector('.pw__card-text');
    if (heading) heading.hidden = true;
    if (text) text.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      this.successEl.focus(); // so screen readers announce the confirmation
    }
  }

  /** @returns {HTMLElement|null} the first invalid field, or null when the form is valid */
  validate() {
    let firstInvalid = null;

    this.form.querySelectorAll('[data-pw-required]').forEach((input) => {
      const value = input.value.trim();
      let message = '';

      if (!value) {
        message = this.strings.required || 'This field is required.';
      } else if (input.hasAttribute('data-pw-email') && !ProductWaitlistForm.isValidEmail(value)) {
        message = this.strings.invalidEmail || 'Please enter a valid email address.';
      } else if (input.hasAttribute('data-pw-phone') && !ProductWaitlistForm.isValidPhone(value)) {
        message = this.strings.invalidPhone || 'Please enter a valid phone number.';
      }

      if (message) {
        this.setFieldError(input, message);
        if (!firstInvalid) firstInvalid = input;
      } else {
        this.clearFieldError(input);
      }
    });

    // Optional phone: only validate when the shopper actually typed something.
    const phone = this.form.querySelector('[data-pw-phone]:not([data-pw-required])');
    if (phone && phone.value.trim() && !ProductWaitlistForm.isValidPhone(phone.value)) {
      this.setFieldError(phone, this.strings.invalidPhone || 'Please enter a valid phone number.');
      if (!firstInvalid) firstInvalid = phone;
    }

    return firstInvalid;
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    this.clearApiError();

    // Silent bot bounce — fake success, send nothing.
    if (this.honeypot && this.honeypot.value.trim() !== '') {
      this.showSuccess();
      return;
    }

    // Dwell-time gate — a real person needs more than 1.5s to fill five fields.
    if (Date.now() - this.mountedAt < 1500) {
      this.showSuccess();
      return;
    }

    const firstInvalid = this.validate();
    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    if (!this.endpoint) {
      // Misconfigured (no Apps Script URL) — don't leave the shopper hanging.
      this.showApiError(this.strings.genericError || 'Something went wrong. Please try again later.');
      return;
    }

    const formData = new FormData(this.form);
    this.setLoading(true);

    fetch(this.endpoint, {
      method: 'POST',
      mode: 'no-cors',
      body: formData,
    })
      .then(() => {
        this.showSuccess();
      })
      .catch(() => {
        this.showApiError(this.strings.genericError || 'Something went wrong. Please try again later.');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }
}

customElements.define('product-waitlist-form', ProductWaitlistForm);
